// THE INBOX (BUILD-BRIEF-R2 §7) — every thread is a touchpoint, and nothing is sent without a
// click.
//
// TWO PANES, the same instrument as the Review queue, because the same question is being
// asked: which of these needs me, and what exactly happens if I act. The rail lists threads and
// unknown senders one line each; the stand shows the selected thread at full dress with the
// draft that answers it, and the verbs that dispose of it.
//
// WHAT THIS PAGE WILL NOT DO. There is no Compose. The only way a message leaves this
// application is an `outbox` row an operator already held as a `draft` proposal, sent with the
// button in the stand's foot — and with the stub sender that button writes an `.eml` to
// `data/outbox/` and marks the row `written`, which the page says in words rather than
// pretending the mail went out.
//
// HONEST COUNTS. Every rail cell is a real count or it reads "not collected". A thread with no
// person behind it shows the address the mail carried, never an invented name.
import { useCallback, useEffect, useState } from 'react';

import EmptyState from '../components/EmptyState.js';
import {
  Att, Fig, Mast, PageShell, Rail, RailCell, Ref, Sep, Sheet, Tag, Zone, ZoneHead,
  type RailTone,
} from '../components/PageKit.js';
import { useToast } from '../components/Toast.js';
import { usePoll } from '../components/usePoll.js';
import { useLocale, useT } from '../i18n/index.js';
import { open as openRoute, ROUTES } from '../routes.js';
import {
  getThread, listOutbox, listThreads, listTriage, mailHealth, mailSummary, sendOutbox,
  triageAttach, triageCreatePerson, triageIgnore, type ThreadDetail,
} from '../api/mail.js';
import { triageMessage } from '../api/transcripts.js';
import { listPersons } from '../api/persons.js';
import type { MailThread, OutboxItem, Person } from '../../shared/types.js';

import './InboxPage.de.js';
import './InboxPage.fr.js';

type Tab = 'needs_me' | 'unknown' | 'all';

/** The one flag a thread row carries: the worst thing known about it, in the record's words. */
function threadFlag(thread: MailThread): { word: string; tone: 'attend' | 'broken' | 'mute' } {
  if (thread.last_direction === 'in') {
    const days = thread.waiting_days ?? 0;
    if (days >= 3) return { word: 'us · late', tone: 'broken' };
    return { word: 'us', tone: 'attend' };
  }
  return { word: 'them', tone: 'mute' };
}

export default function InboxPage() {
  const t = useT();
  const { fmtDate, fmtDateTime, fmtNum, tp } = useLocale();
  const toast = useToast();

  const [tab, setTab] = useState<Tab>('needs_me');
  const [threadKey, setThreadKey] = useState<string | null>(null);
  const [triageId, setTriageId] = useState<number | null>(null);
  const [detail, setDetail] = useState<ThreadDetail | null>(null);
  const [detailError, setDetailError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [attachFor, setAttachFor] = useState<number | null>(null);
  const [attachQuery, setAttachQuery] = useState('');
  const [attachHits, setAttachHits] = useState<Person[]>([]);

  const summaryPoll = usePoll(useCallback(() => mailSummary(), []));
  const healthPoll = usePoll(useCallback(() => mailHealth(), []), 120_000);
  const threadsPoll = usePoll(
    useCallback(() => listThreads({ filter: tab === 'unknown' ? 'all' : tab, limit: 60 }), [tab]),
  );
  const triagePoll = usePoll(useCallback(() => listTriage({ status: 'open' }), []));
  const draftsPoll = usePoll(useCallback(() => listOutbox({ status: 'approved' }), []));
  const outboxPoll = usePoll(useCallback(() => listOutbox({}), []));

  const s = summaryPoll.data;
  const health = healthPoll.data;
  const threads = threadsPoll.data?.items ?? [];
  const triage = triagePoll.data?.items ?? [];
  const drafts = draftsPoll.data?.items ?? [];
  const outbox = (outboxPoll.data?.items ?? []).filter((o) => o.status !== 'approved');

  // The stand follows the rail: a selected thread is fetched, and refetched after any verb.
  useEffect(() => {
    if (!threadKey) { setDetail(null); setDetailError(null); return; }
    let alive = true;
    setDetailError(null);
    getThread(threadKey)
      .then((d) => { if (alive) setDetail(d); })
      .catch((err: unknown) => { if (alive) setDetailError(err instanceof Error ? err.message : String(err)); });
    return () => { alive = false; };
  }, [threadKey, busy]);

  const refreshAll = useCallback(async () => {
    await Promise.allSettled([
      summaryPoll.refresh(), threadsPoll.refresh(), triagePoll.refresh(),
      draftsPoll.refresh(), outboxPoll.refresh(),
    ]);
  }, [summaryPoll, threadsPoll, triagePoll, draftsPoll, outboxPoll]);

  const run = useCallback(async (what: () => Promise<unknown>, ok: string) => {
    setBusy(true);
    try {
      await what();
      toast('ok', ok);
      await refreshAll();
    } catch (err) {
      toast('err', err instanceof Error ? err.message : String(err));
    } finally {
      setBusy(false);
    }
  }, [refreshAll, toast]);

  const selectedTriage = triage.find((r) => r.id === triageId) ?? null;

  // ── the attach picker ─────────────────────────────────────────────────────
  useEffect(() => {
    if (attachFor === null || attachQuery.trim().length < 2) { setAttachHits([]); return; }
    let alive = true;
    listPersons({ q: attachQuery.trim(), limit: 8 })
      .then((r) => { if (alive) setAttachHits(r.items); })
      .catch(() => { if (alive) setAttachHits([]); });
    return () => { alive = false; };
  }, [attachFor, attachQuery]);

  // ── the rail's five cells ─────────────────────────────────────────────────
  const tone = (n: number | null | undefined, attendAbove = 0): RailTone => {
    if (n === null || n === undefined) return 'unknown';
    if (n === 0) return 'nil';
    return n > attendAbove ? 'attend' : 'ink';
  };

  const senderNote = health
    ? health.mode === 'stub'
      ? t('sender in stub mode · approved mail is written to {dir}', { dir: 'data/outbox/' })
      : t('sender is SMTP · approved mail actually leaves')
    : t('the sender has not answered yet');

  const inboundNote = health
    ? health.inbound === 'imap' ? t('IMAP idle') : t('drop folder only')
    : t('not collected');

  return (
    <PageShell>
      <Mast
        kicker={t('Overview')}
        title={t('Inbox')}
        sub={
          <>
            {health?.imap?.host ? <Ref>{health.imap.host}</Ref> : <Ref>{health?.drop_dir ?? 'data/mail/drop'}</Ref>}
            <Sep />{inboundNote}
            <Sep />
            {health?.last_sync_at
              ? <>{t('synced')} <Fig>{fmtDateTime(health.last_sync_at)}</Fig></>
              : <>{t('synced')} <span className="fv--unknown">{t('not collected')}</span></>}
            <Sep />{t('every thread is a touchpoint')}
            <Sep /><span className="mast-notice">{senderNote}</span>
          </>
        }
        verbs={
          <>
            <button type="button" className="btn btn-ghost" onClick={() => { void refreshAll(); }}>
              {t('Refresh')}
            </button>
            <button type="button" className="btn" onClick={() => setTab('unknown')}>
              {t('Unknown senders')} <Fig>{s ? s.unknown_senders : '—'}</Fig> →
            </button>
          </>
        }
      />

      <Rail>
        <RailCell
          label={t('Needs me')}
          value={s ? fmtNum(s.needs_me) : t('not collected')}
          tone={tone(s?.needs_me)}
          onClick={() => setTab('needs_me')}
          pressed={tab === 'needs_me'}
          detail={s
            ? t('threads whose last message came in and is unanswered')
            : t('the summary has not answered yet')}
        />
        <RailCell
          label={t('Unknown')}
          value={s ? fmtNum(s.unknown_senders) : t('not collected')}
          tone={tone(s?.unknown_senders)}
          onClick={() => setTab('unknown')}
          pressed={tab === 'unknown'}
          detail={s
            ? t('{n} with a domain that matches an organisation', {
                n: triage.filter((r) => r.organisation_match_id !== null).length,
              })
            : t('the summary has not answered yet')}
        />
        <RailCell
          label={t('Drafts')}
          value={s ? fmtNum(s.drafts) : t('not collected')}
          tone={tone(s?.drafts)}
          detail={s ? t('held proposals waiting for a send') : t('the summary has not answered yet')}
        />
        <RailCell
          label={t('Outbox')}
          value={s ? fmtNum(s.outbox) : t('not collected')}
          unit={health?.mode === 'stub' ? t('stub') : undefined}
          tone={s ? (s.outbox === 0 ? 'nil' : 'ink') : 'unknown'}
          detail={health?.mode === 'stub'
            ? t('written to data/outbox/, not sent')
            : t('sent through SMTP')}
        />
        <RailCell
          label={t('Threads · 7d')}
          value={s ? fmtNum(s.threads_7d) : t('not collected')}
          tone={s ? (s.threads_7d === 0 ? 'nil' : 'ink') : 'unknown'}
          detail={s
            ? t('{in} in · {out} out · median reply {h}', {
                in: s.messages_in, out: s.messages_out,
                h: s.median_reply_hours === null ? t('not collected') : `${s.median_reply_hours}h`,
              })
            : t('the summary has not answered yet')}
        />
      </Rail>

      {/* ── the two panes ─────────────────────────────────────────────────── */}
      <Zone>
        {(threadsPoll.notice || triagePoll.notice) && (
          <ZoneHead label={t('The mailbox')} note={threadsPoll.notice ?? triagePoll.notice} />
        )}
        {threadsPoll.error && !threadsPoll.data ? (
          <EmptyState
            kind="error"
            title={t('The mailbox could not be read')}
            detail={threadsPoll.error}
            onRetry={() => { void threadsPoll.refresh(); }}
          />
        ) : (
          <Sheet strong clip className="panes">
            <div className="qrail">
              <div className="qtabs">
                <button type="button" className={`qtab${tab === 'needs_me' ? ' on' : ''}`} onClick={() => setTab('needs_me')}>
                  {t('Needs me')} <span className="n">{s ? s.needs_me : '—'}</span>
                </button>
                <button type="button" className={`qtab${tab === 'unknown' ? ' on' : ''}`} onClick={() => setTab('unknown')}>
                  {t('Unknown')} <span className="n">{s ? s.unknown_senders : '—'}</span>
                </button>
                <button type="button" className={`qtab${tab === 'all' ? ' on' : ''}`} onClick={() => setTab('all')}>
                  {t('All')}
                </button>
              </div>

              <div style={{ overflowY: 'auto', flex: 1 }}>
                {tab === 'unknown' ? (
                  triage.length ? triage.map((row) => (
                    <button
                      type="button"
                      key={row.id}
                      className={`qrow${row.id === triageId ? ' on' : ''}`}
                      onClick={() => { setTriageId(row.id); setThreadKey(null); }}
                    >
                      <span />
                      <span>
                        <span className="qk">{row.display_name ?? row.address}</span>
                        <span className="qs">{row.address}</span>
                        <span className="qm">
                          <Fig>{fmtDate(row.last_seen)}</Fig> · {tp(row.message_count, '{n} message', '{n} messages')}
                          {row.organisation_name ? ` · ${row.organisation_name}` : ''}
                        </span>
                      </span>
                      <span className="qflag attend">{t('unknown')}</span>
                    </button>
                  )) : (
                    <p className="zone-empty">{t('No sender is waiting for a judgement.')}</p>
                  )
                ) : (
                  threads.length ? threads.map((thread) => {
                    const flag = threadFlag(thread);
                    return (
                      <button
                        type="button"
                        key={thread.thread_key}
                        className={`qrow${thread.thread_key === threadKey ? ' on' : ''}`}
                        onClick={() => { setThreadKey(thread.thread_key); setTriageId(null); }}
                      >
                        {thread.person_id ? <Att kind="held" /> : <span />}
                        <span>
                          <span className="qk">
                            {thread.person_name ?? t('unknown sender')}
                            {thread.organisation_name ? ` · ${thread.organisation_name}` : ''}
                          </span>
                          <span className="qs">{thread.subject ?? t('(no subject)')}</span>
                          <span className="qm">
                            <Fig>{fmtDate(thread.last_at)}</Fig> · {tp(thread.message_count, '{n} message', '{n} messages')}
                          </span>
                        </span>
                        <span className={`qflag ${flag.tone}`}>{t(flag.word)}</span>
                      </button>
                    );
                  }) : (
                    <p className="zone-empty">
                      {tab === 'needs_me'
                        ? t('Nothing is waiting on you. Every thread was last answered by you.')
                        : t('No mail has been imported yet. Drop an .eml into the drop folder, or configure MAIL_IMAP_HOST.')}
                    </p>
                  )
                )}
              </div>
            </div>

            {/* ── the stand ── */}
            <div className="stand">
              {selectedTriage ? (
                <>
                  <div className="instr-band" style={{ borderTop: 0 }}>
                    <span className="ag-dot ag-dot--attend" />
                    <span className="band-item"><b>{t('Unknown sender')}</b></span>
                    <span className="band-item">
                      <Ref>{selectedTriage.address}</Ref>
                      {selectedTriage.organisation_name
                        ? <> {t('matches')} <b>{selectedTriage.organisation_name}</b> {t('by domain')}</>
                        : <> {t('matches no organisation on the record')}</>}
                    </span>
                    <span className="ml-auto flex gap-8">
                      <button
                        type="button"
                        className="btn btn-sm"
                        disabled={busy}
                        onClick={() => { void run(() => triageCreatePerson(selectedTriage.id), t('Person created and the thread linked.')); }}
                      >
                        {t('Create person')}
                      </button>
                      <button
                        type="button"
                        className="btn btn-sm btn-ghost"
                        disabled={busy}
                        onClick={() => { setAttachFor(selectedTriage.id); setAttachQuery(''); }}
                      >
                        {t('Attach to existing…')}
                      </button>
                      <button
                        type="button"
                        className="btn btn-sm btn-ghost"
                        disabled={busy}
                        onClick={() => { void run(() => triageIgnore(selectedTriage.id, 'domain'), t('The domain is ignored.')); }}
                      >
                        {t('Ignore domain')}
                      </button>
                    </span>
                  </div>

                  <div className="stbody">
                    <p className="rat">
                      {t('This address has written {n} times since {date}. Nothing about it has entered the record: no person, no interaction. Creating a person files a proposal in your name and holds it in the same click, so the ledger says who decided.', {
                        n: selectedTriage.message_count, date: fmtDate(selectedTriage.first_seen),
                      })}
                    </p>
                    {attachFor === selectedTriage.id && (
                      <div className="sec">
                        <div className="lbl"><span className="u-label">{t('Attach to an existing person')}</span></div>
                        <input
                          className="search-input"
                          placeholder={t('Name, organisation, email…')}
                          value={attachQuery}
                          onChange={(e) => setAttachQuery(e.target.value)}
                        />
                        <div className="sheet sheet--clip" style={{ marginTop: 8 }}>
                          {attachHits.length ? attachHits.map((person) => (
                            <button
                              type="button"
                              key={person.id}
                              className="row row--btn"
                              style={{ gridTemplateColumns: '1fr auto' }}
                              disabled={busy}
                              onClick={() => {
                                void run(
                                  () => triageAttach(selectedTriage.id, person.id),
                                  t('The address is now on {name}.', { name: person.name }),
                                ).then(() => setAttachFor(null));
                              }}
                            >
                              <span><span className="w">{person.name}</span></span>
                              <Ref>{person.ref}</Ref>
                            </button>
                          )) : (
                            <p className="zone-empty">
                              {attachQuery.trim().length < 2
                                ? t('Type at least two characters.')
                                : t('No person matches that.')}
                            </p>
                          )}
                        </div>
                      </div>
                    )}
                  </div>
                </>
              ) : detailError ? (
                <div className="stbody">
                  <EmptyState
                    kind="error"
                    title={t('The thread could not be read')}
                    detail={detailError}
                    onRetry={() => { setDetailError(null); setThreadKey(threadKey); }}
                  />
                </div>
              ) : !detail ? (
                <div className="stbody"><p className="rat">{t('Choose a thread on the left.')}</p></div>
              ) : (
                <>
                  <div className="sthd">
                    <span className="kind">{detail.thread.subject ?? t('(no subject)')}</span>
                    {detail.thread.person_ref && <Ref>{detail.thread.person_ref}</Ref>}
                    {detail.thread.person_name}
                    <Sep />
                    <Fig>{detail.thread.message_count}</Fig> {t('messages')}
                    {detail.thread.organisation_name ? <><Sep />{detail.thread.organisation_name}</> : null}
                    <span className="ml-auto">
                      {detail.thread.last_direction === 'in'
                        ? <Tag tone={(detail.thread.waiting_days ?? 0) >= 3 ? 'broken' : 'attend'}>
                            {t('ball · us')}
                            {detail.thread.waiting_days !== null
                              ? <> · <Fig unit={t('d')}>{detail.thread.waiting_days}</Fig></>
                              : null}
                          </Tag>
                        : <Tag>{t('ball · them')}</Tag>}
                    </span>
                  </div>

                  <div style={{ overflowY: 'auto', flex: 1 }}>
                    {[...detail.messages].reverse().map((message) => (
                      <div key={message.id} className={`msg${message.direction === 'out' ? ' msg--out' : ''}`}>
                        <div className="who">
                          <b style={{ color: 'var(--ink-1)' }}>{message.from_name ?? message.from_addr ?? t('unknown sender')}</b>
                          {message.from_addr && <Ref>{message.from_addr}</Ref>}
                          <span className="ml-auto voice-figure">{fmtDateTime(message.sent_at)}</span>
                        </div>
                        <p className="voice-record" style={{ margin: 0, whiteSpace: 'pre-wrap' }}>
                          {message.text_body ?? t('(this message carried no text part)')}
                        </p>
                      </div>
                    ))}

                    {detail.drafts.map((draft) => (
                      <div key={draft.id} className="msg">
                        <div className="ghost">
                          <div className="who">
                            <Att kind="proposed" />
                            <b>{t('Draft reply')}</b>
                            {draft.proposal_ref && <Ref>{draft.proposal_ref}</Ref>}
                            <span className="ml-auto voice-figure">
                              {(draft.text_body ?? '').trim().split(/\s+/).filter(Boolean).length} {t('words')}
                            </span>
                          </div>
                          <p style={{ whiteSpace: 'pre-wrap' }}>{draft.text_body}</p>
                        </div>
                      </div>
                    ))}
                  </div>

                  {/* No draft yet and the ball is theirs → offer one reading of the thread. The
                      run PROPOSES; nothing it writes reaches the record or the outbox by itself.
                      `messages` arrives oldest-first from the route, so the newest is the last. */}
                  {detail.drafts.length === 0 && detail.thread.last_direction === 'in' && detail.messages.length > 0 && (
                    <div className="stfoot">
                      <button
                        type="button"
                        className="btn"
                        disabled={busy}
                        onClick={() => {
                          void run(
                            () => triageMessage(detail.messages[detail.messages.length - 1].id),
                            t('Reading the thread — what it finds arrives in Review.'),
                          );
                        }}
                      >
                        {t('Draft reply')}
                      </button>
                    </div>
                  )}

                  {detail.drafts.length > 0 && (
                    <div className="stfoot">
                      <button
                        type="button"
                        className="btn btn-accent"
                        disabled={busy}
                        onClick={() => { void run(() => sendOutbox(detail.drafts[0].id), t('The message was written to the outbox.')); }}
                      >
                        {health?.mode === 'stub' ? t('Send (stub)') : t('Send')}
                      </button>
                      {detail.drafts[0].proposal_id && (
                        <button
                          type="button"
                          className="btn"
                          onClick={() => openRoute(ROUTES.review({ proposal: detail.drafts[0].proposal_id! }))}
                        >
                          {t('Amend in Review →')}
                        </button>
                      )}
                      <span className="spacer" />
                      <span style={{ color: 'var(--ink-3)', fontSize: 'var(--t-meta)' }}>
                        {health?.mode === 'stub'
                          ? t('writes an .eml into data/outbox/ and marks the row written')
                          : t('leaves through SMTP and marks the row sent')}
                      </span>
                    </div>
                  )}
                </>
              )}
            </div>
          </Sheet>
        )}
      </Zone>

      {/* ── drafts waiting ────────────────────────────────────────────────── */}
      <Zone>
        <ZoneHead
          label={t('Drafts to approve')}
          count={drafts.length}
          note={t('held proposals · nothing here has been sent')}
          route={
            <button type="button" className="zone-route" onClick={() => openRoute(ROUTES.review({ kind: 'draft' }))}>
              {t('Review →')}
            </button>
          }
        />
        {drafts.length ? (
          <Sheet clip>
            {drafts.map((draft) => (
              <DraftRow key={draft.id} draft={draft} busy={busy} onSend={() => {
                void run(() => sendOutbox(draft.id), t('The message was written to the outbox.'));
              }} />
            ))}
          </Sheet>
        ) : (
          <EmptyState
            kind="empty"
            title={t('No draft is waiting')}
            detail={t('A row appears here when you hold a `draft` proposal: the applier writes one outbox row, status approved. Holding a draft is not sending it — sending is the button on the row.')}
          />
        )}
      </Zone>

      {/* ── outbox and sent ───────────────────────────────────────────────── */}
      <Zone>
        <ZoneHead
          label={t('Outbox and sent')}
          count={outbox.length}
          endNote={health?.mode === 'stub' ? t('stub sender · nothing left this machine') : t('SMTP sender')}
        />
        {outbox.length ? (
          <Sheet clip>
            <table className="tbl">
              <thead>
                <tr>
                  <th>{t('Subject')}</th>
                  <th>{t('To')}</th>
                  <th>{t('Status')}</th>
                  <th className="num">{t('Sent')}</th>
                  <th>{t('File')}</th>
                </tr>
              </thead>
              <tbody>
                {outbox.map((row) => (
                  <tr key={row.id}>
                    <td>{row.subject ?? t('(no subject)')}</td>
                    <td>{row.to_addrs.join(', ')}</td>
                    <td>
                      <Tag tone={row.status === 'failed' ? 'broken' : row.status === 'sent' ? 'ok' : 'neutral'}>
                        {t(row.status)}
                      </Tag>
                    </td>
                    <td className="num"><Fig>{fmtDateTime(row.sent_at)}</Fig></td>
                    <td>{row.eml_path ? <Ref>{row.eml_path}</Ref> : <span className="fv--unknown">{t('not collected')}</span>}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </Sheet>
        ) : (
          <EmptyState
            kind="empty"
            title={t('Nothing has been sent')}
            detail={t('A row lands here after you send an approved draft. With the stub sender the bytes are written to data/outbox/ and the row reads written, not sent — the difference is the point.')}
          />
        )}
      </Zone>
    </PageShell>
  );
}

/** One draft, with the two verbs that dispose of it. Extracted so the zone reads as a list. */
function DraftRow({ draft, busy, onSend }: { draft: OutboxItem; busy: boolean; onSend: () => void }) {
  const t = useT();
  const { fmtDateTime } = useLocale();
  return (
    <div className="row" style={{ gridTemplateColumns: '1fr 200px auto' }}>
      <span>
        <span className="w">{draft.subject ?? t('(no subject)')}</span>
        <span className="d"> · {draft.to_addrs.join(', ')}</span>
        <br />
        <span className="d">{(draft.text_body ?? '').slice(0, 160)}</span>
      </span>
      <span className="d">
        {draft.proposal_ref ? <Ref>{draft.proposal_ref}</Ref> : null}{' '}
        <Fig>{fmtDateTime(draft.created_at)}</Fig>
      </span>
      <span className="flex gap-8">
        <button type="button" className="btn btn-sm" disabled={busy} onClick={onSend}>{t('Send')}</button>
        {draft.proposal_id && (
          <button
            type="button"
            className="btn btn-sm btn-ghost"
            onClick={() => openRoute(ROUTES.review({ proposal: draft.proposal_id! }))}
          >
            {t('Amend →')}
          </button>
        )}
      </span>
    </div>
  );
}
